import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import {
  Award,
  Gift,
  Music,
  Video,
  GamepadIcon,
  GraduationCap,
  Cloud,
  ShoppingCart,
  Utensils,
  Plane,
  Smartphone,
  Star,
  Users,
  Target,
  Smile,
  Crown,
  Sun,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
  Coffee,
  Copy,
  Check
} from "lucide-react";
import { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Benefit } from "@/types/Benefit";
import { ScrollArea } from "@/components/ui/scroll-area";
import supabase from "@/utils/supabaseClient";
import {Poll} from "@/types/Poll.ts";
import BenefitBadge from "@/components/BenefitBadge";
import {SEO} from "@/components/SEO.tsx";
import * as React from "react";

interface Category {
  id: string;
  name: string;
  description: string;
  type: 'product' | 'badge';
}

interface Subcategory {
  id: string;
  category_id: string;
  name: string;
  description: string;
}

interface CryptoAddress {
  coin: string;
  symbol: string;
  address: string;
  tag?: string;
}

const getCategoryIcon = (categoryName: string) => {
  const icons: { [key: string]: React.ReactNode } = {
    'Subscription Discounts': <Music className="h-5 w-5" />,
    'Product & Service Discounts': <ShoppingCart className="h-5 w-5" />,
    'Promotion Codes': <Gift className="h-5 w-5" />,
    'Digital Content': <Video className="h-5 w-5" />,
    'Gaming & Community': <GamepadIcon className="h-5 w-5" />,
    'Fun & Lifestyle': <Smartphone className="h-5 w-5" />,
    'Achievement Badges': <Award className="h-5 w-5" />,
    'Participation Badges': <Users className="h-5 w-5" />,
    'Challenge Badges': <Target className="h-5 w-5" />,
    'Fun Badges': <Smile className="h-5 w-5" />,
    'Level & Prestige Badges': <Crown className="h-5 w-5" />,
    'Seasonal Badges': <Sun className="h-5 w-5" />,
    'Community Badges': <MessageCircle className="h-5 w-5" />
  };
  return icons[categoryName] || <Star className="h-5 w-5" />;
};

const Benefits = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [showDonationAddresses, setShowDonationAddresses] = useState(false);
  const [copiedCoin, setCopiedCoin] = useState<string | null>(null);

  const cryptoAddresses: CryptoAddress[] = [
    { coin: "Bitcoin", symbol: "BTC", address: "1BLyrQ4QagBucSG6fXAMiTJjRF231DvTXk" },
    { coin: "Ethereum", symbol: "ETH", address: "0x7247b07224b33e2d559ed7bfd1395a35566edddd" },
    { coin: "Ripple", symbol: "XRP", address: "rNxp4h8apvRis6mJf9Sh8C6iRxfrDWN7AV", tag: "438069054" },
    { coin: "Cardano", symbol: "ADA", address: "addr1v83vfdjhrwlra9g5mxz3g804mzc6l9y59x2hwymw03zqheq7xnw9s" },
    { coin: "Solana", symbol: "SOL", address: "BTuYS2pJSup2b1WqLwcbNEu6v4dFB879eVy4WS8HQNM3" }
  ];

  const handleCopyAddress = (address: string, symbol: string) => {
    navigator.clipboard.writeText(address);
    setCopiedCoin(symbol);
    setTimeout(() => {
      setCopiedCoin(null);
    }, 2000);
  };

  const badgeImages = {
    "Legend Badge": "/lovable-uploads/898a531b-96b1-4947-82ed-69b0191a87fa.png",
    "Brave Heart": "/lovable-uploads/afbf2c1f-dec1-4bd0-be44-d6a39a66efc5.png",
    "Chaos Creator": "/lovable-uploads/a1799587-9828-4324-bb37-0781cd7c12f4.png",
    "Community Leader": "/lovable-uploads/2a108480-bea8-4eae-be6d-ffbc283a16cf.png",
    "Elite Badge": "/lovable-uploads/bdc95533-9aa9-496a-8940-d5f19b1ba379.png",
    "Event Participant": "/lovable-uploads/4a74009c-b499-4c01-be3b-8a3165f15ac3.png",
    "First Step": "/lovable-uploads/e50f6ca2-eec6-42a3-aa91-45869f22252b.png",
    "Halloween Master": "/lovable-uploads/4bc87556-9b2f-48f8-89fa-743dddf3c755.png",
    "Lucky Streak": "/lovable-uploads/706312be-2219-4c0f-b133-bbb64cdfbf58.png",
    "Marathon Runner": "/lovable-uploads/050ee0c6-6815-45e4-80b5-b672b9cc493d.png",
    "Winter Prophet": "/lovable-uploads/fc5d04a0-bd75-4260-be6d-69db9dbfce98.png",
    "Novice Badge": "/lovable-uploads/039533f8-87cd-4f92-afdc-e9317552a86f.png",
    "Unlucky Hero": "/lovable-uploads/0b1515d5-7bef-4ab1-af80-997c8268d0be.png",
    "Night Owl": "/lovable-uploads/f4162cbb-7a47-415a-a397-cf0817c5d0be.png",
    "Master Badge": "/lovable-uploads/4edcef8f-2806-4485-b6a5-50fb529d23b7.png",
    "Oracle": "/lovable-uploads/93d2f389-e867-44d0-84d9-70fffcbbda61.png",
    "Sharp Mind": "/lovable-uploads/7a779c9b-9f1a-4abb-bb18-df8e7b8c5ce0.png",
    "Poll Master": "/lovable-uploads/6e729466-fae7-4e67-a93a-a437d7abca2c.png",
    "Annual Legend": "/lovable-uploads/898a531b-96b1-4947-82ed-69b0191a87fa.png",
    "Summer Sun": "/lovable-uploads/e191f8b5-fa76-44d4-855b-3edc8fffe748.png",
    "Supreme Sage Badge": "/lovable-uploads/2a10bc90-a5e7-4d1e-9547-23b889943fb6.png",
    "Valentine's Badge": "/lovable-uploads/3c0f768d-1259-4805-9081-f2a4c427a908.png",
    "New Year Prophet": "/lovable-uploads/7428a749-e4e2-48ca-b61b-6bff5441d16f.png",
    "Summer Badge": "/lovable-uploads/e191f8b5-fa76-44d4-855b-3edc8fffe748.png"
  };

  const { data: categories, isLoading: isCategoriesLoading } = useQuery({
    queryKey: ['benefit-categories'],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/benefits/categories`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data: Category[] = await response.json();
      // console.log("benefit categories:", data)
      if (data && data.length > 0 && !selectedCategory) {
        setSelectedCategory(data[0].id);
      }
      return data;
    }
  });

  const { data: subcategories, isLoading: isSubCategoriesLoading } = useQuery({
    queryKey: ['benefit-subcategories',selectedCategory],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/benefits/subcategories`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data: Subcategory[] = await response.json();
      // console.log("benefit categories:", data)
      return data;
    }
  });

  const { data: benefits, isLoading } = useQuery({
    queryKey: ['benefits', selectedCategory, selectedSubcategory],
    enabled: !!selectedCategory,
    queryFn: async () => {
      let url = `${import.meta.env.VITE_API_URL}/benefits?categoryId=${selectedCategory}`
      if (selectedSubcategory !== "all") {
        url = url + `&subcategoryId=${selectedSubcategory}`
      }
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data: Benefit[] = await response.json();
      // console.log("benefit categories:", data)
      return data;
    }
  });

  const filteredBenefits = benefits?.filter(benefit =>
    benefit.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    benefit.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedCategoryName = categories?.find(c => c.id === selectedCategory)?.name;

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO
          title="Benefits"
          description="Discover the benefits of participating in polls and joining clans"
          type="website"
          name="blankpoll"
          imageUrl="/blankpoll-banner.jpg"
      />
      <Navigation />

      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto mb-8 mt-16">
          <h1 className="text-4xl font-bold text-center mb-4 text-secondary dark:text-white">Benefits</h1>
          <p className="text-secondary-light dark:text-gray-300 text-center">
            Discover exclusive rewards, discounts, and badges that you can earn through your participation and achievements.
          </p>
        </div>

        <div className="block md:hidden mb-4">
          <Input
            placeholder="Search benefits..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-white dark:bg-gray-800 mb-4"
          />

          {subcategories?.length > 0 && (
            <Select value={selectedSubcategory} onValueChange={setSelectedSubcategory}>
              <SelectTrigger className="bg-white dark:bg-gray-800 mb-4">
                <SelectValue placeholder="Select subcategory" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-gray-800">
                <SelectItem value="all">All Subcategories</SelectItem>
                {subcategories.map((sub) => (
                  <SelectItem key={sub.id} value={sub.id}>{sub.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 bg-white dark:bg-gray-800 shadow-sm rounded-full"
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <div
              className="overflow-hidden"
              ref={scrollContainerRef}
              onScroll={handleScroll}
            >
              <div className="flex gap-3 py-4 px-8">
                {categories?.map((category) => (
                  <Button
                    key={category.id}
                    variant="outline"
                    onClick={() => {
                      setSelectedCategory(category.id);
                      setSelectedSubcategory("all");
                    }}
                    className={`shrink-0 ${
                      selectedCategory === category.id
                      ? "bg-primary text-white dark:bg-primary-dark"
                      : "bg-white dark:bg-gray-800 text-secondary-light dark:text-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {getCategoryIcon(category.name)}
                      <span>{category.name}</span>
                    </div>
                  </Button>
                ))}
              </div>
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 bg-white dark:bg-gray-800 shadow-sm rounded-full"
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          <div className="hidden md:block md:w-64 space-y-6">
            <Card className="overflow-hidden border-dashed border-2 border-gray-200 dark:border-gray-700">
              <div className="bg-[#E5F5EA] dark:bg-[#1F2937] p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Coffee className="h-6 w-6 text-[#155231] dark:text-white" />
                  <h3 className="font-semibold text-lg text-[#155231] dark:text-white">Support Our Benefits Program</h3>
                </div>
                <p className="text-sm mb-4 text-[#155231] dark:text-white">
                  Our polls will always be free, but we need your support to expand our benefits program and offer more exclusive rewards to our community.
                </p>
                <Button
                  className="w-full bg-[#469857] text-white hover:bg-[#469857]/80 dark:bg-primary dark:text-white"
                  onClick={() => setShowDonationAddresses(!showDonationAddresses)}
                >
                  {showDonationAddresses ? "Hide Donation Options" : "Support Us"}
                </Button>
              </div>

              {showDonationAddresses && (
                <div className="p-4 bg-white dark:bg-gray-800">
                  <h4 className="text-sm font-medium mb-3 text-secondary dark:text-white">Cryptocurrency Donations</h4>
                  <div className="space-y-3">
                    {cryptoAddresses.map((crypto) => (
                      <div key={crypto.symbol} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-md">
                        <div className="text-sm font-medium text-secondary dark:text-white">
                          {crypto.coin} ({crypto.symbol})
                          {crypto.tag && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Tag: {crypto.tag}
                            </div>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyAddress(crypto.address, crypto.symbol)}
                        >
                          {copiedCoin === crypto.symbol ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            <Card className="p-4 shadow-md border border-gray-200 dark:border-gray-700 dark:bg-gray-800">
              <div className="space-y-4">
                <h3 className="font-semibold text-secondary dark:text-white">Filters</h3>
                <Input
                  placeholder="Search benefits..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-white dark:bg-gray-700"
                />

                {subcategories?.length > 0 && (
                  <Select value={selectedSubcategory} onValueChange={setSelectedSubcategory}>
                    <SelectTrigger className="bg-white dark:bg-gray-700">
                      <SelectValue placeholder="Select subcategory" />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-gray-800">
                      <SelectItem value="all">All Subcategories</SelectItem>
                      {subcategories.map((sub) => (
                        <SelectItem key={sub.id} value={sub.id}>{sub.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-4 mt-6">
                <h3 className="font-semibold text-secondary dark:text-white">Categories</h3>
                <nav className="space-y-1">
                  {categories?.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => {
                        setSelectedCategory(category.id);
                        setSelectedSubcategory("all");
                      }}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                        selectedCategory === category.id
                        ? "bg-primary text-white dark:bg-primary-dark"
                        : "hover:bg-muted text-secondary-light dark:text-gray-300 bg-white dark:bg-gray-700 dark:hover:bg-gray-600"
                      }`}
                    >
                      {getCategoryIcon(category.name)}
                      <span>{category.name}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </Card>
          </div>

          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {isLoading ? (
                <p className="col-span-full text-center py-8 text-secondary dark:text-white">Loading benefits...</p>
              ) : !selectedCategory ? (
                <p className="col-span-full text-center text-muted-foreground py-8">
                  Select a category to view benefits
                </p>
              ) : filteredBenefits?.length === 0 ? (
                <p className="col-span-full text-center text-muted-foreground py-8">
                  No benefits found for the selected filters.
                </p>
              ) : (
                <TooltipProvider>
                  {filteredBenefits?.map((benefit) => (
                    <Card key={benefit.id} className="flex flex-col shadow-lg bg-white dark:bg-gray-800 relative h-[320px]">
                      <CardHeader className="text-center pt-10">
                        <div className="absolute top-4 right-4 flex items-center gap-2">
                          <Tooltip>
                            <TooltipTrigger>
                              <div
                                className={`rounded-full w-3 h-3 ${
                                  benefit.status === 'active' ? 'bg-green-500' :
                                  benefit.status === 'soon' ? 'bg-orange-500' : 'bg-red-500'
                                }`}
                              />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="capitalize">{benefit.status}</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        {selectedCategoryName?.includes('Badge') && badgeImages[benefit.title] ? (
                          <BenefitBadge name={benefit.title} imagePath={badgeImages[benefit.title]} />
                        ) : (
                          <div className="flex justify-center mb-4">
                            {selectedCategoryName?.includes('Badge') ? (
                              <Award className="h-12 w-12 text-primary" />
                            ) : (
                              <Gift className="h-12 w-12 text-primary" />
                            )}
                          </div>
                        )}
                        <CardTitle className="text-center text-lg">{benefit.title}</CardTitle>
                      </CardHeader>
                      <CardContent className="flex-1 text-center px-6 overflow-y-auto">
                        <CardDescription className="text-center text-sm">
                          {benefit.description}
                        </CardDescription>
                      </CardContent>
                      <CardFooter className="p-6 mt-auto">
                        <Button
                          disabled
                          variant="secondary"
                          className="w-full disabled:text-white"
                        >
                          {selectedCategoryName?.includes('Badge') ? 'Collect Badge' : 'Collect Reward'}
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </TooltipProvider>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Benefits;
